import { FunctionComponent } from "react";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

export interface TargetFieldBadgesData {
	isRequired?: boolean;
	isMulti?: boolean;
	isReadOnly?: boolean;
	isCalculated?: boolean;
	/** Writable only when the record is first created (HubSpot record-source details). */
	isCreateOnly?: boolean;
	/**
	 * The picklist rejects any value outside `options` (Salesforce `restrictedPicklist`). An
	 * unrestricted picklist accepts and stores a new value, so only this flag means "closed set".
	 */
	isRestricted?: boolean;
	/** A Dataverse relationship attribute (Lookup/Customer/Owner/PartyList). */
	isRelationship?: boolean;
	type?: string;
	options?: { label: string; value: string }[];
}

interface TargetFieldBadgesProps {
	field: TargetFieldBadgesData;
	labels: {
		required: string;
		multi: string;
		dropdown: string;
		number: string;
		calculated: string;
		readOnly: string;
		/** Optional: only HubSpot has create-only targets today. */
		createOnly?: string;
		createOnlyTooltip?: string;
		/** Optional: only Salesforce reports restricted picklists today. */
		restricted?: string;
		/** Optional: only Dynamics 365 flags relationship attributes today. */
		relationship?: string;
	};
	testIdPrefix: string;
}

const isDropdownType = (type?: string, options?: { label: string; value: string }[]): boolean => {
	if (options && options.length > 0) {
		return true;
	}

	if (!type) {
		return false;
	}

	const t = type.toLowerCase();

	return t === "enum" || t === "picklist" || t === "select" || t === "dropdown";
};

const isNumberType = (type?: string): boolean => {
	if (!type) {
		return false;
	}

	const t = type.toLowerCase();

	return t === "number" || t === "integer" || t === "double" || t === "currency";
};

const badgeBase = "text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0";

export const TargetFieldBadges: FunctionComponent<TargetFieldBadgesProps> = ({
	field,
	labels,
	testIdPrefix,
}) => {
	const dropdown = isDropdownType(field.type, field.options);
	const number = isNumberType(field.type);

	return (
		<div className="inline-flex items-center gap-1 flex-wrap">
			{field.isRequired && (
				<span
					className={classNames(badgeBase, "bg-status-error-light text-status-error")}
					data-testid={`${testIdPrefix}-badge-required`}
				>
					{labels.required}
				</span>
			)}
			{field.isMulti && (
				<span
					className={classNames(badgeBase, "bg-purple-100 text-purple-800")}
					data-testid={`${testIdPrefix}-badge-multi`}
				>
					{labels.multi}
				</span>
			)}
			{field.isCalculated && (
				<span
					className={classNames(badgeBase, "bg-status-warning-light text-status-warning")}
					data-testid={`${testIdPrefix}-badge-calculated`}
				>
					{labels.calculated}
				</span>
			)}
			{field.isCreateOnly && labels.createOnly && (
				<Tooltip content={labels.createOnlyTooltip ?? ""} disabled={!labels.createOnlyTooltip}>
					<span
						className={classNames(badgeBase, "bg-blue-action-200 text-blue-action cursor-help")}
						data-testid={`${testIdPrefix}-badge-create-only`}
					>
						{labels.createOnly}
					</span>
				</Tooltip>
			)}
			{field.isReadOnly && !field.isCalculated && !field.isCreateOnly && (
				<span
					className={classNames(badgeBase, "bg-status-warning-light text-status-warning")}
					data-testid={`${testIdPrefix}-badge-read-only`}
				>
					{labels.readOnly}
				</span>
			)}
			{field.isRestricted && labels.restricted && (
				<span
					className={classNames(badgeBase, "bg-status-info-light text-status-info")}
					data-testid={`${testIdPrefix}-badge-restricted`}
				>
					{labels.restricted}
				</span>
			)}
			{field.isRelationship && labels.relationship && (
				<span
					className={classNames(badgeBase, "bg-status-info-light text-status-info")}
					data-testid={`${testIdPrefix}-badge-relationship`}
				>
					{labels.relationship}
				</span>
			)}
			{dropdown && (
				<span
					className={classNames(
						badgeBase,
						"bg-brand-white text-text-meta border border-border-light"
					)}
					data-testid={`${testIdPrefix}-badge-dropdown`}
				>
					{labels.dropdown}
				</span>
			)}
			{number && !dropdown && (
				<span
					className={classNames(
						badgeBase,
						"bg-brand-white text-text-meta border border-border-light"
					)}
					data-testid={`${testIdPrefix}-badge-number`}
				>
					{labels.number}
				</span>
			)}
		</div>
	);
};
